/**
 * useMenuStore — 로그인 사용자 메뉴 트리 Zustand 스토어.
 *
 * 【역할】
 * - /api/auth/login 또는 /me 응답의 `menus` 배열을 트리 구조로 보관.
 * - AppSidebar / TabBar 가 구독하여 사이드바 메뉴 렌더링 + 펼침 상태 관리.
 * - authStore.logout() 시 reset() 으로 초기화.
 *
 * 【State】
 *   - tree: MenuNode[] — 정렬·중첩 완료된 트리 루트 배열
 *   - loaded: boolean — 백엔드 응답 1회 이상 받았는지 여부 (로딩 스피너 분기)
 *   - openKeys: string[] — 현재 펼쳐진 부모 메뉴 키 (path 우선, 없으면 key)
 *
 * 【Actions】
 *   - setTreeFromBackend(raw): void — 평탄 배열을 트리로 변환 후 저장 + openKeys 초기화
 *   - setOpenKeys(keys): void — Sidebar 펼침 상태 직접 set
 *   - expandAll(): void — 모든 부모 노드 펼침
 *   - collapseAll(): void — 모두 접기
 *   - reset(): void — 로그아웃 시 비우기
 *
 * 【부수효과】 없음 (영속화 없음 — 로그인 시 매번 재구성).
 * 【사용처】 AppSidebar / TabBar / authStore.logout().
 * 【포팅 원본】 vue-spring-fw `useMenuStore.ts` (Pinia → Zustand 전환).
 */
import { create } from 'zustand'

/** 트리 렌더링용 정규화된 메뉴 노드. */
export interface MenuNode {
  /** 백엔드 menuId(문자열) 또는 raw.key — 트리 식별자. */
  key: string
  /** 표시 라벨 (현재 로케일 적용 후 fallback). */
  label: string
  /** i18n 라벨 키 — TabBar 가 `t(labelKey, label)` 로 다국어 적용. */
  labelKey?: string
  /** 아이콘 키 (Ant Design Icon 매핑명). */
  icon?: string
  /** 라우터 경로. 리프 노드는 필수, 부모 노드는 보통 미정. */
  path?: string
  /** 하위 메뉴. 리프는 필드 자체가 제거됨. */
  children?: MenuNode[]
  /** true 면 사이드바 toItems 단계에서 필터링 (라우트는 유효). */
  hidden?: boolean
}

/** 백엔드 menus 배열 1행 — menuId/key, parentId, sortOrder, menuName, icon, menuPath 등 가변 키 허용. */
export interface RawMenuRow {
  menuId?: string | number
  key?: string
  menuName?: string
  label?: string
  labelKey?: string
  icon?: string
  menuPath?: string
  path?: string
  parentId?: string | number | null
  sortOrder?: number
}

interface MenuState {
  /** 정렬·중첩 완료된 트리 루트 배열. */
  tree: MenuNode[]
  /** 백엔드 응답 1회 이상 수신 여부. */
  loaded: boolean
  /** Sidebar 펼침 상태 (path 또는 key). */
  openKeys: string[]
  /** 백엔드 raw 배열을 트리로 변환·저장. */
  setTreeFromBackend: (raw: RawMenuRow[]) => void
  /** 펼침 상태 직접 지정. */
  setOpenKeys: (keys: string[]) => void
  /** 모든 부모 노드 펼치기. */
  expandAll: () => void
  /** 모든 부모 노드 접기. */
  collapseAll: () => void
  /** 트리/펼침 상태 모두 초기화 (로그아웃). */
  reset: () => void
}

/**
 * 평탄 배열(parentId 참조)을 다중 루트 트리로 변환.
 *
 * @param raw - 백엔드 menus 배열. menuId/key, parentId, sortOrder 등 가변 키 허용.
 * @returns sortOrder 오름차순 정렬된 루트 노드 배열. 리프는 children 필드 제거.
 */
type InternalMenuNode = MenuNode & { _parentId?: string | null; _sortOrder?: number }

function buildTree(raw: RawMenuRow[]): MenuNode[] {
  if (!Array.isArray(raw) || raw.length === 0) return []
  const byId = new Map<string, InternalMenuNode>()
  for (const row of raw) {
    const id = String(row.menuId ?? row.key ?? '')
    if (!id) continue
    byId.set(id, {
      key: id,
      label: String(row.menuName ?? row.label ?? id),
      labelKey: row.labelKey ?? undefined,
      icon: row.icon ?? undefined,
      path: row.menuPath ?? row.path ?? undefined,
      children: [],
      _parentId: row.parentId != null ? String(row.parentId) : null,
      _sortOrder: Number(row.sortOrder ?? 0)
    })
  }
  const roots: InternalMenuNode[] = []
  for (const node of byId.values()) {
    const parentId = node._parentId
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children!.push(node)
    } else {
      roots.push(node)
    }
  }
  const sortRec = (arr: InternalMenuNode[]) => {
    arr.sort((a, b) => (a._sortOrder ?? 0) - (b._sortOrder ?? 0))
    for (const n of arr) {
      if (n.children && n.children.length) sortRec(n.children as InternalMenuNode[])
      else delete n.children
      delete n._parentId
      delete n._sortOrder
    }
  }
  sortRec(roots)
  return roots
}

/**
 * 트리 순회로 children 을 가진 노드의 식별 키만 수집 (expandAll 용).
 *
 * @param nodes - 루트 또는 하위 트리.
 * @returns path(우선) 또는 key 의 평탄 배열.
 */
function collectParentKeys(nodes: MenuNode[]): string[] {
  const keys: string[] = []
  for (const n of nodes) {
    if (n.children?.length) {
      keys.push(n.path ?? n.key)
      keys.push(...collectParentKeys(n.children))
    }
  }
  return keys
}

export const useMenuStore = create<MenuState>((set, get) => ({
  tree: [],
  loaded: false,
  openKeys: [],
  setTreeFromBackend: (raw) => {
    const tree = buildTree(raw)
    set({ tree, loaded: true, openKeys: [] })
  },
  setOpenKeys: (keys) => set({ openKeys: keys }),
  expandAll: () => set({ openKeys: collectParentKeys(get().tree) }),
  collapseAll: () => set({ openKeys: [] }),
  reset: () => set({ tree: [], loaded: false, openKeys: [] })
}))
